import { FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Skeleton } from 'antd';
import { AlertIcon } from '@dynamika-shared/ui/icons';
import { NoContent } from '@dynamika-shared/ui/NoContent/NoContent';
import { SubjectStatus } from '@dynamika-shared/types/subjects';
import { useValidationSpecialistQuestionnairesSlice } from '@restore/ValidationSpecialistQuestionnaires/ValidationSpecialistQuestionnaires.slice';
import { useValidationSpecialistSubjectsSlice } from '@restore/ValidationSpecialistSubjects/ValidationSpecialistSubjects.slice';
import { isVSSchema } from '@utils/validationSpecialist';
import { UploadsHeader } from './components/UploadsHeader';
import { UploadCard } from './components/UploadCard';

interface VSUploadsProps {
  visitId: string;
  visitDefinitionId: string;
  visitName: string;
  visitStatus: SubjectStatus;
}

export const VSUploads: FC<VSUploadsProps> = ({
  visitId,
  visitDefinitionId,
  visitName,
  visitStatus,
}) => {
  const { clinicalTrialId } = useParams<{ clinicalTrialId: string }>();

  const [uploads, uploadsLoading, fetchUploads] = useValidationSpecialistSubjectsSlice(
    (s, a) => [
      s.uploads,
      s.uploadsLoading,
      a.fetchValidationSpecialistVisitUploads,
    ]
  );
  
  const [schemas, schemasLoading, needUpdateUploads, getEDTFSchemas, setNeedUpdateUploads] = useValidationSpecialistQuestionnairesSlice(
    (s, a) => [
      s.schemas,
      s.schemasLoading,
      s.needUpdateUploads,
      a.getEDTFSchemas,
      a.setNeedUpdateUploads
    ]
  );

  const dataType = uploads[0]?.dataType;

// Initial fetch on mount
useEffect(() => {
  if (!clinicalTrialId || !visitId) return;

  fetchUploads({ clinicalTrialId, visitId });
}, [clinicalTrialId, visitId]);

// Triggered refetch when needUpdateUploads toggles to true
useEffect(() => {
  if (!clinicalTrialId || !visitId || !needUpdateUploads) return;
  fetchUploads({ clinicalTrialId, visitId });

  setNeedUpdateUploads(false);
}, [needUpdateUploads, clinicalTrialId, visitId]);

  useEffect(() => {
    if (!clinicalTrialId || !dataType) return;
    
    getEDTFSchemas({ clinicalTrialId, dataType, visitDefinitionId });
  }, [clinicalTrialId, dataType, visitDefinitionId, getEDTFSchemas]);
  
  const isEmpty = !uploadsLoading && uploads.length === 0;
  const hasUploads = !uploadsLoading && uploads.length > 0;

  const vsSchema = schemas.find(isVSSchema);

  const isSchemaUnavailable = !schemasLoading && !vsSchema;

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex-1 overflow-auto">
        <UploadsHeader visitName={visitName} visitStatus={visitStatus} />

        {uploadsLoading && <Skeleton active />}

        {isEmpty && (
          <NoContent
            iconName="noContent"
            title={
              <span className="text-1424 text-spanishGray">
                <span className="font-semibold text-chineseBlack">
                  No files uploaded for this visit yet
                </span>
              </span>
            }
          >
            <div />
          </NoContent>
        )}

        {hasUploads && (
          <div className="w-full max-w-[1050px] mx-auto space-y-4">
            {isSchemaUnavailable && (
              <Alert
                type="warning"
                showIcon
                icon={<AlertIcon className="mr-1 h-2 w-2 !text-vermilion" />}
                className="items-center p-1.5 font-medium border-redOrange border-opacity-40 !bg-seashell !mb-2"
                description="Participant Form unavailable — it hasn’t been set up for this study. Please contact technical support."
              />
            )}

            {uploads.map(upload => (
              <UploadCard
                key={upload.id}
                uploadFormSchema={vsSchema}
                upload={upload}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
